import { BACKEND_ENV_HEADER } from '@/utils/appEnv';

export class AIFetchError extends Error {
  constructor(
    message: string,
    public readonly status: number | undefined,
    public readonly retryable: boolean,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'AIFetchError';
  }
}

const RETRYABLE_STATUSES = [429, 503, 504];

async function extractErrorBody(response: Response): Promise<{ code?: string; message: string }> {
  try {
    const data = (await response.json()) as {
      error?: { code?: string; message?: string };
      detail?: unknown;
    };
    if (data.error) {
      return {
        code: data.error.code,
        message: data.error.message ?? 'Couldn’t get a reply. Please try again.',
      };
    }
    if (typeof data.detail === 'string') return { message: data.detail };
  } catch {
    // body is not JSON
  }
  return { message: 'Couldn’t get a reply. Please try again.' };
}

export type SSEEventCallback = (eventName: string, data: Record<string, unknown>) => void;

export async function postSSE(
  url: string,
  body: unknown,
  onEvent: SSEEventCallback,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...BACKEND_ENV_HEADER },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new AIFetchError('Network error', undefined, true);
  }

  if (!response.ok) {
    const status = response.status;
    const retryable = RETRYABLE_STATUSES.includes(status);
    const { code, message } = await extractErrorBody(response);
    throw new AIFetchError(message, status, retryable, code);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.startsWith('text/event-stream')) {
    // Edge is buffering or rewriting the SSE — let the caller fall back
    throw new AIFetchError('SSE unavailable', response.status, true);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new AIFetchError('No response body', undefined, false);

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on blank lines; keep trailing incomplete frame in buffer
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        if (!frame.trim()) continue;

        let eventName = 'message';
        let dataLine = '';

        for (const line of frame.split('\n')) {
          if (line.startsWith(':')) continue; // keep-alive
          if (line.startsWith('event:')) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataLine = line.slice(5).trim();
          }
        }

        if (!dataLine) continue;
        try {
          const parsed = JSON.parse(dataLine) as Record<string, unknown>;
          onEvent(eventName, parsed);
        } catch {
          // malformed data — skip frame
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function fetchJSON<T>(url: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers: { ...init.headers, ...BACKEND_ENV_HEADER },
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new AIFetchError(
      'Couldn’t reach the assistant. Please check your connection and try again.',
      undefined,
      true,
    );
  }

  if (!response.ok) {
    const status = response.status;
    const retryable = RETRYABLE_STATUSES.includes(status);
    const { code, message } = await extractErrorBody(response);
    throw new AIFetchError(message, status, retryable, code);
  }

  return response.json() as Promise<T>;
}
